<template>
  <div class="flex h-full grow flex-col">
    <main class="flex-1 estimation-page-layout">
      <div class="estimation-content-wrapper">
        
        <!-- Header -->
        <div class="estimation-header-section">
          <div class="header-content">
            <h1>
              Estimación por Equipo
            </h1>
            <p>
              Asignar miembros del equipo al proyecto y estimar sus horas de trabajo.
            </p>
          </div>
          <div class="header-actions">
            <div class="action-group">
              <button 
                @click="saveBudget"
                :disabled="!isFormValid"
                :class="[
                  'w-full sm:w-auto transition-all duration-200',
                  isFormValid 
                    ? 'btn btn-primary' 
                    : 'btn btn-primary opacity-50 cursor-not-allowed'
                ]"
              >
                <i class="material-icons ">save</i>
                <span class="truncate">Guardar Estimación</span>
              </button>
              <p v-if="!isFormValid" class="text-sm text-orange-600 dark:text-orange-400 text-right whitespace-nowrap">
                Complete los campos obligatorios (*)
              </p>
            </div>
          </div>
        </div>

        <!-- Project Info Form -->
        <div class="estimation-project-info">
          <h2 class="dark:text-slate-200">Información del Proyecto</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5" for="teamClientName">
                Nombre del cliente <span class="text-red-500 dark:text-red-400">*</span>
              </label>
              <input 
                id="teamClientName"
                v-model="projectInfo.client"
                type="text" 
                class="form-control dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100 dark:placeholder-slate-400"
                placeholder="Ej. Acme Corp"
                required
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5" for="teamOwnerName">
                Quien arma la estimación <span class="text-red-500 dark:text-red-400">*</span>
              </label>
              <input 
                id="teamOwnerName"
                v-model="projectInfo.name"
                type="text" 
                class="form-control dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100 dark:placeholder-slate-400"
                placeholder="Ej. Juan Pérez"
                required
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5" for="teamJiraTicket">JIRA Ticket</label>
              <input 
                id="teamJiraTicket"
                v-model="projectInfo.jiraTicket"
                type="text" 
                class="form-control dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100 dark:placeholder-slate-400"
                placeholder="Ej. https://portinos.atlassian.net/browse/XX-XXXX"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5" for="teamIntranetUrl">Intranet URL</label>
              <input 
                id="teamIntranetUrl"
                v-model="projectInfo.intranetUrl"
                type="url" 
                class="form-control dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100 dark:placeholder-slate-400"
                placeholder="Ej. https://intranet.portinos.com/index.php/main/ficha_request/XXXXX"
              />
            </div>
          </div>
        </div>

        <!-- Team Assignment Table -->
        <div class="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div class="p-6 border-b border-slate-200 dark:border-slate-700">
            <h2 class="text-slate-900 dark:text-slate-100 text-lg font-semibold">Estimación por Equipo</h2>
          </div>
          
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gradient-to-r from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/30 border-b border-orange-200 dark:border-orange-700">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-200 dark:text-orange-300 uppercase tracking-wider">Equipo</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-200 dark:text-orange-300 uppercase tracking-wider">Rol</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-200 dark:text-orange-300 uppercase tracking-wider">Recurso</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-200 dark:text-orange-300 uppercase tracking-wider">Fecha de Inicio</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-200 dark:text-orange-300 uppercase tracking-wider">Fecha de Fin</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-200 dark:text-orange-300 uppercase tracking-wider">Días</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-200 dark:text-orange-300 uppercase tracking-wider">% Asignación</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-200 dark:text-orange-300 uppercase tracking-wider">Horas Estimadas</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-200 dark:text-orange-300 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody class="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                <tr v-for="(member, index) in teamMembers" :key="index">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <select 
                      v-model="member.teamId"
                      @change="onTeamChange(index)"
                      class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    >
                      <option :value="null">Seleccionar equipo</option>
                      <option 
                        v-for="equipo in equipos" 
                        :key="equipo.id" 
                        :value="equipo.id"
                      >
                        {{ equipo.name }}
                      </option>
                    </select>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <select 
                      v-model="member.roleId"
                      @change="onRoleChange(index)"
                      :disabled="!member.teamId"
                      class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option :value="null">{{ member.teamId ? 'Seleccionar rol' : 'Primero selecciona equipo' }}</option>
                      <option 
                        v-for="role in getFilteredRoles(member.teamId)" 
                        :key="role.id" 
                        :value="role.id"
                      >
                        {{ role.name }}
                      </option>
                    </select>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <select 
                      v-model="member.personId"
                      @change="onPersonChange(index)"
                      :disabled="!member.roleId"
                      class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option :value="null">{{ member.roleId ? 'Seleccionar persona' : 'Primero selecciona rol' }}</option>
                      <option 
                        v-for="person in getFilteredPeople(member.roleId)" 
                        :key="person.id" 
                        :value="person.id"
                      >
                        {{ person.name }}
                      </option>
                    </select>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="relative">
                      <input 
                        v-model="member.startDate"
                        @change="calculateDays(index)"
                        type="date" 
                        class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:focus:ring-orange-400 dark:focus:border-orange-400 text-sm date-input-weekdays"
                        title="Only business days count towards project duration (excluding weekends and holidays)"
                      />
                      <div class="text-xs text-slate-500 dark:text-slate-400 mt-1">Excluye fines de semana y feriados</div>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="relative">
                      <input 
                        v-model="member.endDate"
                        @change="calculateDays(index)"
                        type="date" 
                        class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:focus:ring-orange-400 dark:focus:border-orange-400 text-sm date-input-weekdays"
                        title="Only business days count towards project duration (excluding weekends and holidays)"
                      />
                      <div class="text-xs text-slate-500 dark:text-slate-400 mt-1">Excluye fines de semana y feriados</div>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span class="text-sm text-slate-700 dark:text-slate-300 font-medium">{{ member.days }}</span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <input 
                      v-model.number="member.assignment"
                      @input="calculateHours(index)"
                      type="number" 
                      min="0" 
                      max="100"
                      class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:focus:ring-orange-400 dark:focus:border-orange-400 text-sm"
                      placeholder="0-100"
                    />
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span class="text-sm text-slate-700 dark:text-slate-300 font-medium">{{ member.estimatedHours.toFixed(1) }}</span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <button 
                      @click="removeTeamMember(index)"
                      class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm font-medium"                    >
                    <span class="material-icons-round text-sm">delete</span>
                      Eliminar
                    </button>
                  </td>
                </tr>
              </tbody>
              <tfoot class="bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700">
                <tr>
                  <td colspan="7" class="px-6 py-4 text-right text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Total Horas del Equipo:
                  </td>
                  <td class="px-6 py-4 text-sm font-bold text-slate-900">
                    {{ totalHours.toFixed(1) }}
                  </td>
                  <td class="px-6 py-4"></td>
                </tr>
              </tfoot>
            </table>
          </div>
          
          <!-- Add Member Button -->
           <br>
          <div class="px-6 pb-4">
            <button 
              @click="addTeamMember"
              class="w-full flex items-center justify-center gap-2 rounded-lg h-11 px-4 bg-orange-100 dark:bg-orange-900/30 hover:bg-orange-200 dark:hover:bg-orange-800/50 text-orange-700 dark:text-orange-300 text-sm font-semibold transition-colors border border-orange-200 dark:border-orange-700"
            >
              <span class="truncate">+ Agregar Recurso</span>
            </button>
          </div>
        </div>

        <!-- Notes Section -->
        <div class="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 shadow-sm dark:shadow-slate-900/20 p-6">
          <h2 class="text-slate-900 dark:text-slate-100 text-lg font-semibold mb-4">Notas del Proyecto</h2>
          <div>
            <Editor
              v-model="projectInfo.notes"
              :api-key="'rg0qmdu719tlzhg0xuj1v8t9ldw6fccjjvnep9iuh9bv0eai'"
              :init="editorConfig"
            />
          </div>
        </div>
      </div>
    </main>
    
    <!-- Footer -->
    <footer-section />
  </div>
</template>

<script>
import Editor from '@tinymce/tinymce-vue';
import { useToast } from "vue-toastification";
import { isArgentinianHoliday } from '../data/holidays.js';
import FooterSection from '../components/ui/Footer.vue';
import { useAuth } from '@/composables/useAuth'; // Import useAuth
import { adminAPI } from '@/services/api'; // Import API service

export default {
  name: 'TeamAssignmentEstimator',
  components: {
    Editor,
    FooterSection
  },
  setup() {
    const toast = useToast();
    const { getAuthHeader } = useAuth(); // Get getAuthHeader
    return { toast, getAuthHeader }; // Return getAuthHeader
  },
  data() {
    return {
      // Data from backend
      equipos: [],
      roles: [],
      peoples: [],
      
      budgetId: null, // Tracks the current budget ID (null for new budgets)
      projectInfo: {
        name: '',
        client: '',
        jiraTicket: '',
        intranetUrl: '',
        notes: ''
      },
      teamMembers: [
        {
          person: '',
          personId: null,
          team: '',
          teamId: null,
          role: '',
          roleId: null,
          startDate: '',
          endDate: '',
          days: 0,
          assignment: 0,
          estimatedHours: 0
        }
      ],
      isDarkMode: false,
      observer: null,
      loading: false
    }
  },
  computed: {
    isDark() {
      return this.isDarkMode;
    },
    editorConfig() {
      return {
        height: 300,
        menubar: false,
        plugins: [
          'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
          'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
          'insertdatetime', 'media', 'table', 'help', 'wordcount', 'paste',
          'textcolor', 'colorpicker', 'contextmenu'
        ],
        toolbar: 'undo redo | blocks | bold italic underline | forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | table tabledelete | tableprops tablerowprops tablecellprops | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol | removeformat | help',
        // Enhanced paste settings for better table handling
        paste_retain_style_properties: 'border border-color border-width background-color color font-weight text-align',
        paste_word_valid_elements: 'b,strong,i,em,h1,h2,h3,h4,h5,h6,p,ol,ul,li,a[href],span,color,font-size,font-color,font-family,mark,table,tr,td,th,tbody,thead,tfoot',
        paste_webkit_styles: 'border color font-weight text-align background-color',
        paste_merge_formats: true,
        paste_convert_word_fake_lists: true,
        paste_remove_styles_if_webkit: false,
        table_use_colgroups: false,
        table_responsive_width: true,
        table_default_attributes: {
          'border': '1'
        },
        table_default_styles: {
          'border-collapse': 'collapse',
          'width': '100%'
        },
        content_style: this.isDark 
          ? 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px; background-color: #334155; color: #f1f5f9; }'
          : 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
        skin: this.isDark ? 'oxide-dark' : 'oxide',
        icons: 'thin'
      };
    },
    allPeople() {
      // Return all peoples from backend
      return this.peoples.map(p => ({
        id: p.id,
        name: p.name,
        roleId: p.role_id,
        roleName: p.role_name,
        equipoName: p.equipo_name
      }));
    },
    totalHours() {
      return this.teamMembers.reduce((total, member) => total + member.estimatedHours, 0);
    },
    isFormValid() {
      return this.projectInfo.client.trim() !== '' && 
             this.projectInfo.name.trim() !== '';
    }
  },
  methods: {
    // Load data from backend
    async loadEquipos() {
      try {
        const data = await adminAPI.getEquipos({ limit: 1000 });
        if (data.success) {
          this.equipos = data.data || [];
        }
      } catch (error) {
        console.error('Error loading equipos:', error);
        this.toast.error('Error al cargar equipos');
      }
    },
    
    async loadRoles() {
      try {
        const data = await adminAPI.getRoles({ limit: 1000 });
        if (data.success) {
          this.roles = data.data || [];
        }
      } catch (error) {
        console.error('Error loading roles:', error);
        this.toast.error('Error al cargar roles');
      }
    },
    
    async loadPeoples() {
      try {
        const data = await adminAPI.getPeoples({ limit: 1000 });
        if (data.success) {
          this.peoples = data.peoples || data.data || [];
        }
      } catch (error) {
        console.error('Error loading peoples:', error);
        this.toast.error('Error al cargar personas');
      }
    },
    
    async loadAllData() {
      this.loading = true;
      try {
        await Promise.all([
          this.loadEquipos(),
          this.loadRoles(),
          this.loadPeoples()
        ]);
      } finally {
        this.loading = false;
      }
    },
    
    updateDarkModeState() {
      this.isDarkMode = document.documentElement.classList.contains('dark');
    },
    addTeamMember() {
      this.teamMembers.push({
        person: '',
        personId: null,
        team: '',
        teamId: null,
        role: '',
        roleId: null,
        startDate: '',
        endDate: '',
        days: 0,
        assignment: 0,
        estimatedHours: 0
      });
    },
    removeTeamMember(index) {
      this.teamMembers.splice(index, 1);
    },
    // Filter methods for cascading selects
    getFilteredRoles(teamId) {
      if (!teamId) return [];
      return this.roles.filter(role => role.equipo_id === teamId);
    },
    
    getFilteredPeople(roleId) {
      if (!roleId) return [];
      return this.peoples.filter(person => person.role_id === roleId);
    },
    
    onTeamChange(index) {
      const member = this.teamMembers[index];
      // Clear role and person when team changes
      member.roleId = null;
      member.role = '';
      member.personId = null;
      member.person = '';
      
      // Set team name
      if (member.teamId) {
        const equipo = this.equipos.find(e => e.id === member.teamId);
        if (equipo) {
          member.team = equipo.name;
        }
      } else {
        member.team = '';
      }
    },
    
    onRoleChange(index) {
      const member = this.teamMembers[index];
      // Clear person when role changes
      member.personId = null;
      member.person = '';
      
      // Set role name
      if (member.roleId) {
        const role = this.roles.find(r => r.id === member.roleId);
        if (role) {
          member.role = role.name;
        }
      } else {
        member.role = '';
      }
    },
    
    onPersonChange(index) {
      const member = this.teamMembers[index];
      // Set person name
      if (member.personId) {
        const person = this.peoples.find(p => p.id === member.personId);
        if (person) {
          member.person = person.name;
        }
      } else {
        member.person = '';
      }
    },
    
    updatePersonTeamRole(index) {
      const member = this.teamMembers[index];
      if (member.personId) {
        // Find the selected person
        const person = this.peoples.find(p => p.id === member.personId);
        if (person) {
          member.person = person.name;
          
          // Find the role of this person
          const role = this.roles.find(r => r.id === person.role_id);
          if (role) {
            member.roleId = role.id;
            member.role = role.name;
            member.teamId = role.equipo_id;
            
            // Find the team name
            const equipo = this.equipos.find(e => e.id === role.equipo_id);
            if (equipo) {
              member.team = equipo.name;
            }
          }
        }
      } else {
        // Clear team and role if no person is selected
        member.person = '';
        member.team = '';
        member.role = '';
        member.teamId = null;
        member.roleId = null;
      }
    },
    calculateDays(index) {
      const member = this.teamMembers[index];
      if (member.startDate && member.endDate) {
        // Parse dates properly to avoid timezone issues
        // Date inputs give us strings like "2025-06-03", which when parsed with new Date()
        // can be interpreted as UTC and shifted to previous day in local timezone
        const startParts = member.startDate.split('-');
        const endParts = member.endDate.split('-');
        
        const start = new Date(parseInt(startParts[0]), parseInt(startParts[1]) - 1, parseInt(startParts[2]));
        const end = new Date(parseInt(endParts[0]), parseInt(endParts[1]) - 1, parseInt(endParts[2]));
        
        if (end >= start) {
          // Calculate business days (excluding weekends and Argentinian holidays)
          let businessDays = 0;
          const currentDate = new Date(start);
          
          while (currentDate <= end) {
            const dayOfWeek = currentDate.getDay();
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sunday (0) or Saturday (6)
            const isHoliday = isArgentinianHoliday(currentDate);
            
            // Only count if it's not a weekend and not a holiday
            if (!isWeekend && !isHoliday) {
              businessDays++;
            }
            
            currentDate.setDate(currentDate.getDate() + 1);
          }
          
          member.days = businessDays;
          this.calculateHours(index);
        } else {
          member.days = 0;
          member.estimatedHours = 0;
        }
      }
    },
    calculateHours(index) {
      const member = this.teamMembers[index];
      const dailyHours = 6.5;
      const assignmentPercent = member.assignment / 100;
      member.estimatedHours = member.days * dailyHours * assignmentPercent;
    },
    async saveBudget() {
      try {
        const budgetData = {
          type: 'team',
          projectName: this.projectInfo.name,
          clientName: this.projectInfo.client,
          jiraTicket: this.projectInfo.jiraTicket,
          intranetUrl: this.projectInfo.intranetUrl,
          notes: this.projectInfo.notes,
          teamMembers: this.teamMembers.filter(member => member.personId && member.teamId && member.roleId).map(member => ({
            ...member,
            person_id: member.personId,
            team_id: member.teamId,
            role_id: member.roleId
          })),
          totalHours: this.totalHours
        };

        const existingBudgetId = this.$route.query.budgetId || this.$route.query.edit;
        let response;
        const headers = { 
          'Content-Type': 'application/json',
          ...this.getAuthHeader() // Add auth header
        };
        
        const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3001';
        if (existingBudgetId) {
          // Update existing budget using unified API
          response = await fetch(`${apiUrl}/api/budgets/${existingBudgetId}`, {
            method: 'PUT',
            headers, // Use updated headers
            body: JSON.stringify(budgetData)
          });
        } else {
          // Create new budget using unified API
          response = await fetch(`${apiUrl}/api/budgets`, {
            method: 'POST',
            headers, // Use updated headers
            body: JSON.stringify(budgetData)
          });
        }

        if (response.ok) {
          const result = await response.json();
          
          // Update budgetId if this was a new budget creation or update
          if (result.id) {
            this.budgetId = result.id;
          }
          
          // Show success message with Project ID
          const projectIdMsg = this.budgetId ? ` (Project ID: #${this.budgetId})` : '';
          this.toast.success(existingBudgetId ? 
            `¡Presupuesto de equipo actualizado exitosamente!${projectIdMsg}` : 
            `¡Presupuesto de equipo guardado exitosamente!${projectIdMsg}`
          );
          
          // Wait a moment to let user see the Project ID before navigating
          setTimeout(() => {
            this.$router.push('/budgets');
          }, 2000);
        } else {
          throw new Error('Failed to save budget');
        }
      } catch (error) {
        console.error('Error saving budget:', error);
        this.toast.error('Error al guardar el presupuesto. Por favor intenta nuevamente.');
      }
    }
  },
  async mounted() {
    // Load data from backend
    await this.loadAllData();
    
    // Initialize dark mode state and observer
    this.updateDarkModeState();
    this.observer = new MutationObserver(() => {
      this.updateDarkModeState();
    });
    this.observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Check if we're editing an existing budget
    const budgetId = this.$route.query.budgetId || this.$route.query.edit;
    if (budgetId) {
      try {
        const headers = this.getAuthHeader(); // Get auth header
        const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${apiUrl}/api/budgets/${budgetId}`, { headers }); // Add headers to fetch
        if (response.ok) {
          const budget = await response.json();
          
          // Populate form with existing data
          this.budgetId = budget.id;
          this.projectInfo.name = budget.projectName || '';
          this.projectInfo.client = budget.clientName || '';
          this.projectInfo.jiraTicket = budget.jiraTicket || '';
          this.projectInfo.intranetUrl = budget.intranetUrl || '';
          this.projectInfo.notes = budget.notes || '';
          
          // Parse and populate team members
          if (budget.teamMembers) {
            let teamMembers = budget.teamMembers;
            if (typeof teamMembers === 'string') {
              teamMembers = JSON.parse(teamMembers);
            }
            if (Array.isArray(teamMembers) && teamMembers.length > 0) {
              this.teamMembers = teamMembers.map(member => {
                // Ensure we have the IDs and names properly set
                const teamMember = {
                  ...member,
                  personId: member.person_id || member.personId || null,
                  teamId: member.team_id || member.teamId || null,
                  roleId: member.role_id || member.roleId || null
                };
                
                // If we have IDs, populate the names
                if (teamMember.personId && this.peoples.length > 0) {
                  const person = this.peoples.find(p => p.id === teamMember.personId);
                  if (person) {
                    teamMember.person = person.name;
                  }
                }
                
                if (teamMember.teamId && this.equipos.length > 0) {
                  const equipo = this.equipos.find(e => e.id === teamMember.teamId);
                  if (equipo) {
                    teamMember.team = equipo.name;
                  }
                }
                
                if (teamMember.roleId && this.roles.length > 0) {
                  const role = this.roles.find(r => r.id === teamMember.roleId);
                  if (role) {
                    teamMember.role = role.name;
                  }
                }
                
                return teamMember;
              });
            }
          }
        } else {
          this.toast.error('Error al cargar el presupuesto');
        }
      } catch (error) {
        console.error('Error loading budget:', error);
        this.toast.error('Error al cargar el presupuesto');
      }
    }
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}
</script>

<style scoped>
/* Gray out weekends in date picker calendars */
.date-input-weekdays::-webkit-calendar-picker-indicator {
  cursor: pointer;
}

/* Custom CSS to style weekend days in the calendar dropdown */
.date-input-weekdays {
  position: relative;
}

/* This targets the calendar popup for WebKit browsers */
.date-input-weekdays::-webkit-datetime-edit-day-field:disabled,
.date-input-weekdays::-webkit-datetime-edit-month-field:disabled,
.date-input-weekdays::-webkit-datetime-edit-year-field:disabled {
  color: #9ca3af;
}
</style>

<style>
/* Global styles for calendar popup weekends - applies to all date inputs with the class */
input.date-input-weekdays::-webkit-calendar-picker-indicator {
  cursor: pointer;
}

/* Style weekend days in calendar popup */
input[type="date"].date-input-weekdays {
  color-scheme: light;
}

/* Custom calendar styling for better weekend indication */
.date-input-weekdays {
  --weekend-color: #e5e7eb;
  --weekend-text-color: #9ca3af;
}

/* Additional styling to make weekends visually distinct */
input.date-input-weekdays:focus {
  outline: none;
  box-shadow: 0 0 0 2px #3b82f6;
}
</style>
